import axios from 'axios';
import { ApiException } from '../../types';
import { useStore } from '../components/state/zustand-store';
import {
  DriveRoles,
  AdministrationRoles,
  CalendarRoles,
  RulesEngineRoles,
  DirectoryRoles,
  ToolsRoles,
  UserRoles,
  BillingRoles,
  UserViewRoles,
} from './roles/client-roles';

interface ErrorProps {
  error: unknown;
  snackbarMessage?: string;
}

let alreadyReloading = false;

export function handleErrors({ error, snackbarMessage }: ErrorProps) {
  const { setError, setOpenShowable, setSnackbar } = useStore.getState();
  if (alreadyReloading) return;

  if (axios.isAxiosError(error)) {
    const errorResponse = error.response?.data as ApiException;

    if (errorResponse?.header === 'Session Expired') {
      const currentPath = window.location.pathname;
      const redirectUrl =
        currentPath === '/'
          ? '/login?session-timeout=true'
          : `/login?session-timeout=true&from=${encodeURIComponent(
              currentPath
            )}`;
      window.location.replace(redirectUrl);
      alreadyReloading = true;
      return;
    }

    if (errorResponse?.isShowable) {
      setError(errorResponse);
      setOpenShowable(true);
    } else if (errorResponse?.isAccessException) {
      const permission = errorResponse.message?.split(':');
      if (permission && permission?.length > 0) {
        const allRolesArray: {
          name: string;
          value: string;
          type: string;
          group: string;
        }[] = [];
        Object.keys(DriveRoles).forEach((key) =>
          allRolesArray.push(DriveRoles[key as keyof typeof DriveRoles])
        );
        Object.keys(AdministrationRoles).forEach((key) =>
          allRolesArray.push(
            AdministrationRoles[key as keyof typeof AdministrationRoles]
          )
        );
        Object.keys(CalendarRoles).forEach((key) =>
          allRolesArray.push(CalendarRoles[key as keyof typeof CalendarRoles])
        );
        Object.keys(RulesEngineRoles).forEach((key) =>
          allRolesArray.push(
            RulesEngineRoles[key as keyof typeof RulesEngineRoles]
          )
        );
        Object.keys(DirectoryRoles).forEach((key) =>
          allRolesArray.push(DirectoryRoles[key as keyof typeof DirectoryRoles])
        );
        Object.keys(ToolsRoles).forEach((key) =>
          allRolesArray.push(ToolsRoles[key as keyof typeof ToolsRoles])
        );
        Object.keys(UserRoles).forEach((key) =>
          allRolesArray.push(UserRoles[key as keyof typeof UserRoles])
        );
        Object.keys(BillingRoles).forEach((key) =>
          allRolesArray.push(BillingRoles[key as keyof typeof BillingRoles])
        );
        Object.keys(UserViewRoles).forEach((key) =>
          allRolesArray.push(UserViewRoles[key as keyof typeof UserViewRoles])
        );

        const permissionDisplayName =
          allRolesArray.find((role) => role.value === permission[1].trim())
            ?.name || permission[1].trim();

        setSnackbar({
          message: `You need the permission "${permissionDisplayName}" to perform this action.`,
          severity: 'error',
          open: true,
        });
      } else {
        setSnackbar({
          message: `You do not have permission to perform this action.`,
          severity: 'error',
          open: true,
        });
      }
    } else {
      setSnackbar({
        message:
          snackbarMessage || errorResponse?.message || 'An error occurred.',
        severity: 'error',
        open: true,
      });
    }
  } else {
    setSnackbar({
      message:
        snackbarMessage ||
        'Your session has expired. Please refresh the page and try again.',
      severity: 'error',
      open: true,
    });
  }
}
